// Navegación de documento completo segura para el shell nativo (Capacitor):
// recargas (nativeSafeReload) y saltos a otra ruta (nativeSafeNavigate).
//
// Por qué existe: en iOS (WKWebView con server.url remoto) la política de
// navegación de Capacitor (WebViewDelegationHandler.decidePolicyFor) solo
// mantiene DENTRO del WebView una navegación de documento completo si el host
// casa con server.allowNavigation (ausente en los builds iOS ≤ 1.0(5)) o si la
// URL empieza con server.url = https://useredy.com/tablet. Cualquier otra —
// incluido un window.location.reload() parado en /{slug}/pos — se entrega a
// UIApplication.shared.open → Safari, expulsando al operador de la app (bug
// "cambiar la caja abre Safari", 2026-07-25). Las navegaciones del router de
// Next (pushState) NO pasan por esa política; solo los full-document loads.
//
// En Android no ocurre: MainActivity.kt sobreescribe shouldOverrideUrlLoading y
// trata useredy.com como interno. En web/PWA tampoco. Por eso el desvío es SOLO
// nativo-iOS y el resto conserva el reload de siempre.
//
// La salida iOS es una full-nav a /tablet — el único prefijo permitido por la
// política — que re-corre el boot gate y aterriza de vuelta en la superficie
// configurada (mismo patrón que SetupScreen.onCancel y la salida del kiosk en
// AttractScreen). Documento nuevo = mismo contrato que un reload (PinGate y
// POSScreen remontan desde cero); sessionStorage/localStorage sobreviven igual.
//
// OJO: solo para superficies de OPERADOR. Las páginas públicas de cliente
// (/venue, /order, /buy) abren FUERA del shell a propósito
// (__tests__/internal-links-no-blank.test.ts) y no deben usar este helper.
//
// Se lee window.Capacitor (el global que inyecta el bridge nativo) en vez de
// importar @capacitor/core: BuildWatcher monta en el layout raíz (todas las
// páginas, marketing incluido) y el import lo metería al bundle compartido.

type CapacitorGlobal = {
  isNativePlatform?: () => boolean
  getPlatform?: () => string
}

function capacitorGlobal(): CapacitorGlobal | undefined {
  if (typeof window === "undefined") return undefined
  return (window as Window & { Capacitor?: CapacitorGlobal }).Capacitor
}

/** true dentro del shell nativo (Android o iOS). */
export function isNativeShell(): boolean {
  return capacitorGlobal()?.isNativePlatform?.() === true
}

/** true solo dentro del shell nativo iOS (com.kickoff.redy). */
export function isNativeIOS(): boolean {
  const cap = capacitorGlobal()
  return cap?.isNativePlatform?.() === true && cap.getPlatform?.() === "ios"
}

/**
 * true dentro del shell HQ (com.kickoff.redy.hq, el superadmin del founder).
 *
 * Se detecta por el `appendUserAgent: 'RedyHQ'` de su capacitor.config. Importa
 * porque HQ arranca en `server.url = https://useredy.com/superadmin`, NO en
 * /tablet: mandarlo al desvío de arranque del POS lo sacaría de su propia app
 * y lo dejaría en un gate de tablet que no le corresponde. HQ además nació con
 * `allowNavigation` puesto, así que un reload normal se queda dentro del
 * WebView — que es exactamente lo que necesita.
 */
export function isNativeHQ(): boolean {
  if (typeof navigator === "undefined") return false
  return navigator.userAgent.includes("RedyHQ")
}

/** Ruta de arranque del shell — único prefijo de full-nav permitido en iOS. */
export const NATIVE_BOOT_PATH = "/tablet"

/** Query param con el que /tablet reenvía la navegación al destino real. */
export const NATIVE_BOOT_TARGET_PARAM = "to"

/**
 * Decisión pura, separada para testearla sin DOM (patrón INV-2).
 *
 * `hq` desvía SOLO al shell del POS: el shell HQ arranca en /superadmin y no
 * tiene nada que hacer en /tablet (ver isNativeHQ). Omitirlo conserva el
 * comportamiento de siempre para los llamadores que aún no lo pasan.
 */
export function resolveHardReloadTarget(
  nativeIOS: boolean,
  hq = false,
): { kind: "reload" } | { kind: "navigate"; href: string } {
  if (!nativeIOS || hq) return { kind: "reload" }
  return { kind: "navigate", href: NATIVE_BOOT_PATH }
}

/**
 * Ruta interna segura: relativa a la raíz y sin escapar a otro origen.
 * Misma regla que (auth)/callback y auth-link, más `/\` — el navegador
 * normaliza `/\evil.com` a `//evil.com`, así que sin ese caso el reenvío de
 * /tablet sería un open-redirect.
 */
export function isSafeInternalPath(path: string): boolean {
  return path.startsWith("/") && !path.startsWith("//") && !path.startsWith("/\\")
}

/**
 * Destino real de una navegación de documento completo (decisión pura, INV-2).
 *
 * En iOS la política solo deja pasar lo que empieza con server.url
 * (= https://useredy.com/tablet); todo lo demás se va a Safari. Así que un
 * destino de operador se pide a través del propio prefijo permitido
 * (/tablet?to=<destino>) y el boot gate completa el salto con el router
 * (pushState — no pasa por decidePolicyFor). El full-load SIGUE ocurriendo, así
 * que el servidor ve las cookies recién puestas en la primera request, que es
 * la razón por la que estos sitios usan location.href y no router.push.
 *
 * Lo que NO es ruta interna (una URL absoluta a otro dominio) pasa intacto: esa
 * SÍ debe abrir fuera del shell.
 */
export function resolveHardNavTarget(nativeIOS: boolean, path: string): string {
  if (!nativeIOS) return path
  if (!isSafeInternalPath(path)) return path
  // Ya cae bajo el prefijo permitido — mismo criterio que la política nativa
  // (starts(with:) sobre el absoluteString), así que no hace falta el rodeo.
  if (path.startsWith(NATIVE_BOOT_PATH)) return path
  return `${NATIVE_BOOT_PATH}?${NATIVE_BOOT_TARGET_PARAM}=${encodeURIComponent(path)}`
}

/**
 * Aterrizaje del boot gate cuando NO hay sesión (decisión pura, INV-2).
 *
 * Guideline 3.1.1 (App Review 2026-08-03): el shell iOS es login-only — la
 * WelcomeLanding ofrece "Comenzar" → /signup, y para Apple el registro de un
 * negocio es "acceso a mecanismos externos de compra/suscripción". En iOS el
 * arranque (y el logout, que re-corre el gate) cae directo en SetupScreen;
 * Android y web conservan la landing con registro.
 */
export function resolveLoggedOutLanding(nativeIOS: boolean): "setup" | "welcome" {
  return nativeIOS ? "setup" : "welcome"
}

/** Drop-in de `window.location.href = path` para superficies de operador. */
export function nativeSafeNavigate(path: string): void {
  if (typeof window === "undefined") return
  window.location.href = resolveHardNavTarget(isNativeIOS(), path)
}

/** Drop-in de window.location.reload() para superficies de operador. */
export function nativeSafeReload(): void {
  if (typeof window === "undefined") return
  const target = resolveHardReloadTarget(isNativeIOS(), isNativeHQ())
  if (target.kind === "navigate") {
    window.location.href = target.href
    return
  }
  window.location.reload()
}
